import React, { createContext, useContext, useMemo, useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  useAuthStatusQuery,
  useLoginMutation,
  useLogoutMutation,
  useIncarnateMutation,
  useWakeUpMutation,
  useRegistrationAndLoginMutation,
  useGuestRegisterMutation,
  useHashLoginMutation,
  useLoginGoogleMutation,
  useRequestNewPasswordMutation,
  useRestorePasswordMutation,
  authApi,
} from '../authApi';
import { persistAuth, clearAuth, normalizeUser, setIncarnated } from '../persistence';
import { notifySuccess, notifyError } from '../../../services/notifications';
import { setLegacyToken, legacyLogin } from '../legacySync';
import { setLoginModal } from '../../global/globalUiState';

const AuthStateContext = createContext(null);
const AuthActionsContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isTransitioning, setIsTransitioning] = useState(false);

  const authQuery = useAuthStatusQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    skip: isTransitioning || !localStorage.getItem('token'),
  });

  const [login] = useLoginMutation();
  const [logout] = useLogoutMutation();
  const [incarnate] = useIncarnateMutation();
  const [wakeUp] = useWakeUpMutation();
  const [registerAndLogin] = useRegistrationAndLoginMutation();
  const [guestRegister] = useGuestRegisterMutation();
  const [hashLogin] = useHashLoginMutation();
  const [loginGoogle] = useLoginGoogleMutation();
  const [requestNewPassword] = useRequestNewPasswordMutation();
  const [restorePassword] = useRestorePasswordMutation();

  const wrap = useCallback(async (fn, args, onSuccessKey, onErrorKey, skipRedirect = false) => {
    const res = await fn(args);
    if (res && 'data' in res && res.data) {
      const { user, sessionId, roles } = res.data;
      if (sessionId || user || roles) {
        setLegacyToken(sessionId || null);
        legacyLogin({ user, sessionId, roles, loggedIn: true, incarnated: false });
        persistAuth({ sessionId, user, roles });
        dispatch(
          authApi.util.upsertQueryData('status', undefined, {
            user: user && Object.keys(user).length > 0 ? normalizeUser(user) : null,
            sessionId: sessionId ?? null,
            roles: roles ?? null,
            loggedIn: true,
          })
        );
      }
      if (onSuccessKey) notifySuccess(onSuccessKey);
      // After login-like flows, navigate to dashboard; page-level hooks will refetch
      if (!skipRedirect && (fn === login || fn === registerAndLogin || fn === guestRegister || fn === hashLogin || fn === loginGoogle)) {
        setIncarnated(false);
        
        // Close login modal after successful login
        dispatch(setLoginModal(false));
        
        // Check if user was on an invitation page before login
        const pendingInvitationHash = localStorage.getItem('pendingInvitationHash');
        if (pendingInvitationHash) {
          // Clear the stored hash and redirect to invitation page
          localStorage.removeItem('pendingInvitationHash');
          navigate(`/invitation/${pendingInvitationHash}`, { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      }
    } else if (res && 'error' in res && res.error) {
      const errorMessage = res.error?.data?.errMsg || res.error?.data?.response?.errMsg || onErrorKey;
      if (errorMessage) notifyError(errorMessage);
    }
    return res;
  }, [dispatch, navigate, login, registerAndLogin, guestRegister, hashLogin, loginGoogle]);

  const actions = useMemo(() => ({
    login: (args) => wrap(login, args, 'auth.loginSuccess', 'auth.loginFailed'),
    logout: async () => {
      // try { legacyLogout(); } catch (e) { /* noop */ }
      dispatch(
        authApi.util.upsertQueryData('status', undefined, {
          user: null,
          sessionId: null,
          roles: null,
          loggedIn: false,
        })
      );
      clearAuth();
      setIncarnated(false);
      setLegacyToken(null);

      const res = await logout();
      if (res && 'error' in res) {
        notifyError('auth.logoutFailed');
      }
      navigate('/login', { replace: true });
      return res;
    },
    incarnate: async (args) => {
      setIsTransitioning(true);
      try {
        const res = await wrap(incarnate, args, 'auth.incarnateSuccess', 'auth.incarnateFailed');
        if (res && 'data' in res && res.data) {
          const { user, sessionId, roles } = res.data;
          setIncarnated(true);
          try { legacyLogin({ user, sessionId, roles, loggedIn: true, incarnated: true }); } catch (e) { /* noop */ }
          persistAuth({ sessionId, user, roles });
          dispatch(
            authApi.util.upsertQueryData('status', undefined, {
              user: user && Object.keys(user).length > 0 ? normalizeUser(user) : null,
              sessionId: sessionId ?? null,
              roles: roles ?? null,
              loggedIn: true,
            })
          );
        }
        return res;
      } finally {
        setIsTransitioning(false);
      }
    },
    wakeUp: async () => {
      setIncarnated(false);
      setIsTransitioning(true);
      try {
        const res = await wrap(wakeUp, undefined, 'auth.wakeUpSuccess', 'auth.wakeUpFailed');
        if (res && 'data' in res && res.data) {
          const { user, sessionId, roles } = res.data;
          try { legacyLogin({ user, sessionId, roles, loggedIn: true, incarnated: false }); } catch (e) { /* noop */ }
          persistAuth({ sessionId, user, roles });
          dispatch(
            authApi.util.upsertQueryData('status', undefined, {
              user: user && Object.keys(user).length > 0 ? normalizeUser(user) : null,
              sessionId: sessionId ?? null,
              roles: roles ?? null,
              loggedIn: true,
            })
          );
        }
        return res;
      } finally {
        setIsTransitioning(false);
      }
    },
    registerAndLogin: (args) => wrap(registerAndLogin, args, 'auth.accFinalized', 'auth.registerFailed'),
    registerAndLoginWithoutRedirect: (args) => wrap(registerAndLogin, args, 'auth.accFinalized', 'auth.registerFailed', true),
    guestRegister: (args) => wrap(guestRegister, args, 'auth.accFinalized', 'auth.guestRegisterFailed'),
    hashLogin: (args) => wrap(hashLogin, args, 'auth.loginSuccess', 'auth.hashLoginFailed'),
    loginGoogle: (args) => wrap(loginGoogle, args, 'auth.loginSuccess', 'auth.googleLoginFailed'),
    requestNewPassword: async (args) => {
      const res = await wrap(requestNewPassword, args, 'auth.passRequestSucceed', 'auth.loginFailed');
      return res;
    },
    restorePassword: async (args) => {
      const res = await wrap(restorePassword, args, 'auth.passChange');
      if (res && 'data' in res && res.data) {
        navigate('/login', { replace: true });
      }
      return res;
    },
  }), [wrap, login, logout, incarnate, wakeUp, registerAndLogin, guestRegister, hashLogin, loginGoogle, requestNewPassword, restorePassword, navigate, dispatch]);

  const computedData = useMemo(() => {
    const d = authQuery.data || null;
    if (!d) return null;

    const sessionId = d.sessionId || localStorage.getItem('token');
    const loggedIn = d.loggedIn !== undefined ? d.loggedIn : (Boolean(d.user) || Boolean(sessionId));
    const rawUser = d.user;
    const user = rawUser && Object.keys(rawUser).length > 0 ? normalizeUser(rawUser) : null;

    return { ...d, sessionId, loggedIn, user };
  }, [authQuery.data]);

  useEffect(() => {
    if (!computedData || authQuery.isLoading) return;

    const { loggedIn, sessionId, user, roles } = computedData;

    if (loggedIn && sessionId) {
      // User is logged in - sync to legacy and localStorage
      setLegacyToken(sessionId);
      legacyLogin({ ...computedData, loggedIn: true });
      persistAuth({ sessionId, user, roles });
    } else if (loggedIn === false && !sessionId) {
      // User is logged out - clear everything
      clearAuth();
      setIncarnated(false);
      setLegacyToken(null);
      // legacyLogout();
    }
  }, [computedData, authQuery.isLoading]);

  const stateValue = useMemo(() => {
    const hasToken = !!localStorage.getItem('token');
    const isLoading = hasToken ? (authQuery.isLoading || authQuery.isUninitialized) : false;

    return ({
      data: computedData,
      isLoading,
      isTransitioning,
      error: authQuery.error,
      refetch: authQuery.refetch,
    });
  }, [computedData, authQuery.isLoading, authQuery.isUninitialized, authQuery.error, authQuery.refetch, isTransitioning]);


  return (
    <AuthActionsContext.Provider value={actions}>
      <AuthStateContext.Provider value={stateValue}>
        {children}
      </AuthStateContext.Provider>
    </AuthActionsContext.Provider>
  );
};

export const useAuthContext = () => {
  const ctx = useContext(AuthStateContext);
  if (!ctx) throw new Error('useAuthContext must be used within an AuthProvider');
  return ctx;
};

export const useAuthActionsContext = () => {
  const ctx = useContext(AuthActionsContext);
  if (!ctx) throw new Error('useAuthActionsContext must be used within an AuthProvider');
  return ctx;
};
